import { SelMessage } from "@selector/shared/components";
import { addDays, startOfDay } from "date-fns";
import { type FC, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { type InjectedFormProps, reduxForm } from "redux-form";
import {
  ActionButton,
  Callout,
  CalloutIntent,
  Card,
  CardGrid,
  CardGridArea,
  CardSection,
  Copyable,
  VStack,
} from "widgets";

import { getDiffInSeconds, lifeTimeValidation, simpleDateValidation } from "@/app-utils/form.js";
import { generateRestToken } from "@/redux/modules/auth.js";
import type { ReduxDispatch } from "@/redux/redux-utils.js";

import { ReactDateSelectReduxFormField } from "./redux-form/ReactDateSelectorField.js";
import styleLayout from "./styles/PageLayout.module.scss";

enum RestTokenFormNames {
  FORM_NAME = "restTokenForm",
  REST_TOKEN_EXPIRATION_DATE = "restTokenExpirationDate",
}

interface LocalStateProps {
  token?: string;
  error?: string;
}

/**
 * Simple form to request an access token for the REST API.
 */
const AccountSettingsRestTokenPageImpl: FC<InjectedFormProps<any, any>> = (props) => {
  const { handleSubmit, submitting } = props;
  const dispatch = useDispatch<ReduxDispatch>();
  const [state, setState] = useState<LocalStateProps>({ error: null, token: null });

  const onSubmit = (values) => {
    const expirationDate = values[RestTokenFormNames.REST_TOKEN_EXPIRATION_DATE];
    return dispatch(generateRestToken(getDiffInSeconds(expirationDate)))
      .then((token) => {
        setState({
          token: token,
        });
      })
      .catch((err) => {
        setState({ error: err.message });
      });
  };

  const isSelectableDate = useCallback((selected: Date) => {
    const today = startOfDay(new Date());
    const selectedDate = startOfDay(selected);
    if (selectedDate < today || selectedDate > addDays(selectedDate, 365)) {
      return false;
    }
    return true;
  }, []);

  return (
    <CardGrid className={styleLayout.page}>
      <Card
        key="account-settings-rest-token-card"
        title="Generate REST API Token"
        area={CardGridArea.Left}
        style={{ minHeight: 400 }}
      >
        <SelMessage id={"form.restToken.explanationMessage"} />
        <ReactDateSelectReduxFormField
          horizontalLayout={false}
          key={`project-edit-initial-end-date`}
          name={RestTokenFormNames.REST_TOKEN_EXPIRATION_DATE}
          title={"Expiration date (Limited to one year)"}
          withTime={false}
          placeholder="Choose an expiration date"
          validate={[simpleDateValidation, lifeTimeValidation]}
          isSelectableDate={isSelectableDate}
        />
        <Callout intent={CalloutIntent.Danger}>{state.error}</Callout>
        <ActionButton
          isLoading={submitting}
          onPress={handleSubmit(onSubmit)}
          type={"submit"}
          label={"Generate Token"}
        />
        <VStack spacing={20}>
          {state.token && (
            <CardSection title={"Generated Token"}>
              <Copyable
                title={() => "Copy token to clipboard"}
                getTextContent={async () => state.token!}
                hideContent={() => true}
              >
                <div style={{ wordBreak: "break-all" }}>{state.token}</div>
              </Copyable>
            </CardSection>
          )}
        </VStack>
      </Card>
    </CardGrid>
  );
};

const CreateTokenForm = reduxForm({
  form: RestTokenFormNames.FORM_NAME,
  initialValues: {
    [RestTokenFormNames.REST_TOKEN_EXPIRATION_DATE]: new Date().toISOString(),
  },
})(AccountSettingsRestTokenPageImpl) as unknown;

export { CreateTokenForm };
